import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  type?: 'website' | 'article';
  publishedAt?: string;
  updatedAt?: string;
  author?: string;
  keywords?: string[];
}

export function SEO({
  title,
  description,
  canonical,
  image = '/og-image.jpg',
  type = 'website',
  publishedAt,
  updatedAt,
  author,
  keywords = []
}: SEOProps) {
  const siteUrl = import.meta.env.VITE_APP_URL || 'https://freelancepay.app';
  const siteName = 'FreelancePay';
  const defaultDescription = 'FreelancePay - The ultimate platform for freelancers to manage invoices, payments, and clients efficiently.';
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{`${title} | ${siteName}`}</title>
      <meta name="description" content={description || defaultDescription} />
      <meta name="keywords" content={keywords.join(', ')} />
      <link rel="canonical" href={canonical || `${siteUrl}${window.location.pathname}`} />

      {/* OpenGraph Meta Tags */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description || defaultDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical || `${siteUrl}${window.location.pathname}`} />
      <meta property="og:image" content={`${siteUrl}${image}`} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description || defaultDescription} />
      <meta name="twitter:image" content={`${siteUrl}${image}`} />

      {/* Article Specific Meta Tags */}
      {type === 'article' && (
        <>
          {publishedAt && <meta property="article:published_time" content={publishedAt} />}
          {updatedAt && <meta property="article:modified_time" content={updatedAt} />}
          {author && <meta property="article:author" content={author} />}
        </>
      )}

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': type === 'article' ? 'Article' : 'WebSite',
          headline: title,
          description: description || defaultDescription,
          image: `${siteUrl}${image}`,
          url: canonical || `${siteUrl}${window.location.pathname}`,
          datePublished: publishedAt,
          dateModified: updatedAt,
          author: author ? {
            '@type': 'Person',
            name: author,
          } : undefined,
          publisher: {
            '@type': 'Organization',
            name: siteName,
            logo: {
              '@type': 'ImageObject',
              url: `${siteUrl}/logo.png`,
            },
          },
        })}
      </script>
    </Helmet>
  );
} 